import React from 'react';
import { useParams } from 'react-router-dom';
import {
  MUIDataTable,
  MUIDataTableColumnDef,
  TableActionTypes,
  TableState,
  useDataTable,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { useGetIndividualRankingDetails } from '../../../api-refactored/group-special-consideration/useGetIndividualRankingDetails';

interface IndividualRankingDetailsTableProps {
  isPsle?: boolean;
  modeOfAssessment: string;
}

const useTableColumns = (
  tableState: TableState,
  t: TFunction<'translation'>,
  modeOfAssessment: string,
  isPsle = false
): MUIDataTableColumnDef[] => {
  const widths = isPsle
    ? ['13%', '13%', '13%', '13%', '12%', '12%', '12%', '12%']
    : ['17%', '17%', '18%', '16%', '16%', '16%'];

  return [
    {
      name: 'indexNo',
      label: 'Index No.',
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
        customBodyRender: (value: any) => value ?? '-',
      },
    },
    {
      name: 'uniFinPassport',
      label: 'NRIC/FIN/Passport No.',
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
      },
    },
    {
      name: 'candidateName',
      label: "Candidate's Name",
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
        customBodyRender: (value: any) => value ?? '-',
      },
    },
    {
      name: 'orderOfMerit',
      label: 'Order of Merit',
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined) {
            return '-';
          }

          return `${tableState.data[dataIndex].orderOfMeritPosition}/${tableState.data[dataIndex].orderOfMeritMaximum}`;
        },
      },
    },
    {
      name: 'subjectForecastGrade',
      label: t('specialConsideration->>TableColumn.SubjectPaperForecastGrade'),
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
      },
    },
    {
      name: 'subjectForecastGradePrelim',
      label: 'Subject Forecast Grade Prelim',
      options: {
        display: isPsle || 'excluded',
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
      },
    },
    {
      name: 'psleMarks',
      label: `PSLE ${
        modeOfAssessment === 'LISTENING_COMPREHENSION'
          ? 'LC'
          : modeOfAssessment[0].concat(modeOfAssessment.slice(1).toLowerCase())
      } Marks`,
      options: {
        display: isPsle || 'excluded',
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
      },
    },

    {
      name: 'affected',
      label: 'Affected or Not Affected?',
      options: {
        setCellProps: (cellValue: any, rowIndex: number, columnIndex: number) => ({
          style: { width: widths[columnIndex] },
        }),
        customBodyRender: (value: any) => (value ? 'Affected' : '-'),
      },
    },
  ];
};

const IndividualRankingDetailsTable = ({
  isPsle = false,
  modeOfAssessment,
}: IndividualRankingDetailsTableProps) => {
  const { uuid } = useParams<{ uuid: string }>();
  const { t } = useTranslation();

  const { state: tableState, dispatch: tableDispatch } = useDataTable();

  const { isLoading } = useGetIndividualRankingDetails({
    uuid,
    onSuccess: (data) =>
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data,
          count: data.length,
        },
      } as TableActionTypes),
  });

  const tableColumns = useTableColumns(tableState, t, modeOfAssessment, isPsle);

  return (
    <MUIDataTable
      state={tableState}
      dispatch={tableDispatch}
      columns={tableColumns}
      data={tableState.data}
      isLoading={isLoading}
    />
  );
};

export default IndividualRankingDetailsTable;
